import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import InputLabel from '@mui/material/InputLabel';
import RadioGroup from '@mui/material/RadioGroup';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useAtomValue } from 'jotai';
import { type SyntheticEvent, useEffect } from 'react';
import {
  Controller,
  FormProvider,
  type SubmitHandler,
  useForm,
  useWatch
} from 'react-hook-form';

import CheckPermissions from '@/components/CheckPermissions/CheckPermissions';
import LoadingIndicator from '@/components/LoadingIndicator';
import { PERMISSIONS } from '@/constants/auth.constants';
import { AUTO_SOC_TIERS } from '@/constants/products.constants';
import TicketFeedbackRating from '@/features/TicketFeedbackRating';
import TicketStatusCloseResolutions from '@/features/TicketStatusCloseResolutions';
import useStaticData from '@/hooks/StaticData.hooks';
import { ticketAtom } from '@/pages/Ticket';
import { useUpdateStatus } from '@/pages/Ticket/Ticket.store';
import type { TicketStatusDialog } from '@/pages/Ticket/Ticket.types';
import { TICKET_STATUS } from '@/pages/Tickets/Tickets.constants';
import { userIsInternalAtom } from '@/store/user.store';

import {
  MIN_MESSAGE_LENGTH,
  TicketThreatOutcome
} from './TicketStatusDialogThreat.constants';
import type { TicketStatusDialogThreatProps } from './TicketStatusDialogThreat.types';
import TicketStatusThreatOutcomePicker from './TicketStatusThreatOutcomePicker';

const TicketStatusDialogThreat = ({
  open,
  setOpen
}: TicketStatusDialogThreatProps) => {
  const { ticket_status, organization, resolution_id } =
    useAtomValue(ticketAtom);
  const userIsInternal = useAtomValue(userIsInternalAtom);

  const { statuses } = useStaticData();
  const { isLoadingUpdateStatus, updateStatus } = useUpdateStatus(setOpen);

  const form = useForm<TicketStatusDialog>({
    defaultValues: {
      outcome: undefined,
      message: '',
      resolution: undefined,
      feedback_rating: undefined,
      feedback_reason: undefined
    }
  });
  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors }
  } = form;
  const formResolution = useWatch({ control, name: 'resolution' });
  const formOutcome = useWatch({ control, name: 'outcome' });
  const formFeedbackRating = useWatch({ control, name: 'feedback_rating' });

  const isAutoSocOrg = organization?.is_auto_soc > AUTO_SOC_TIERS.FORTIFY;
  const isAcknowledged = ticket_status?.name === TICKET_STATUS.ACKNOWLEDGED;
  const isRemediated = ticket_status?.name === TICKET_STATUS.REMEDIATED;
  const internalClosingWithExistingResolution =
    userIsInternal && isRemediated && !!resolution_id;

  // Only from Acknowledged can the ticket be remediated and kept open, and
  // only by non-internal (customer-facing) users; internal users always
  // close a Remediated or Open ticket fully.
  const canRemediate = isAcknowledged && !userIsInternal;

  const showFeedback = !isAutoSocOrg;

  const dialogTitle =
    !canRemediate || formOutcome === TicketThreatOutcome.Close
      ? 'Close Ticket'
      : 'Remediate Ticket';

  // if ticket has an existing resolution preselect it when closing a ticket
  useEffect(() => {
    if (open && resolution_id) {
      setValue('resolution', String(resolution_id));
    }
  }, [open, resolution_id, setValue]);

  const handleCancel = () => {
    reset();
    setOpen(false);
  };

  const handleDialogClose = (
    _event: SyntheticEvent,
    reason: 'backdropClick' | 'escapeKeyDown'
  ) => {
    if (reason === 'backdropClick') {
      return;
    }

    handleCancel();
  };

  const onSubmit: SubmitHandler<TicketStatusDialog> = (data) => {
    const newStatusName =
      canRemediate && data.outcome === TicketThreatOutcome.Remediate
        ? TICKET_STATUS.REMEDIATED
        : TICKET_STATUS.COMPLETED;

    const newStatusObject = statuses.find(
      (status) => status.name === newStatusName
    );

    if (newStatusObject) {
      updateStatus({
        message: data.message,
        ticket_status_id: newStatusObject.id,
        resolution_id: parseInt(data.resolution ?? '', 10),
        feedback_rating: data.feedback_rating,
        feedback_reason: data.feedback_reason
      });
    }
  };

  return (
    <Dialog open={open} onClose={handleDialogClose} maxWidth="md" fullWidth>
      <LoadingIndicator isLoading={isLoadingUpdateStatus} />
      <DialogTitle>{dialogTitle}</DialogTitle>
      <DialogContent>
        <FormProvider {...form}>
          <form noValidate>
            <Stack spacing={2.5} sx={{ pt: 2, pb: 2, width: '100%' }}>
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                  Resolution
                </Typography>
                <Controller
                  control={control}
                  name="resolution"
                  rules={{ required: true }}
                  render={({ field, formState }) => (
                    <FormControl
                      disabled={internalClosingWithExistingResolution}
                      sx={{ ml: 2 }}
                    >
                      <RadioGroup
                        name="resolution-radio-group"
                        value={field.value}
                        onChange={(e) => field.onChange(e.target.value)}
                      >
                        <TicketStatusCloseResolutions
                          field={field}
                          hasError={!!formState.errors.resolution}
                        />
                      </RadioGroup>
                    </FormControl>
                  )}
                />
                {errors.resolution && (
                  <FormHelperText error>
                    Please select a resolution.
                  </FormHelperText>
                )}
              </Box>
              {canRemediate && (
                <TicketStatusThreatOutcomePicker resolution={formResolution} />
              )}
              {showFeedback && (
                <CheckPermissions permissions={[PERMISSIONS.NOT_INTERNAL]}>
                  <Controller
                    control={control}
                    name="feedback_rating"
                    render={({ field }) => (
                      <Controller
                        control={control}
                        name="feedback_reason"
                        render={(feedbackReason) => (
                          <TicketFeedbackRating
                            field={field}
                            feedbackReason={feedbackReason}
                            formFeedbackRating={formFeedbackRating}
                          />
                        )}
                      />
                    )}
                  />
                </CheckPermissions>
              )}
              <Box>
                <InputLabel htmlFor="message-input">Message *</InputLabel>
                <Controller
                  control={control}
                  name="message"
                  rules={{ required: true, minLength: MIN_MESSAGE_LENGTH }}
                  render={({ field, formState }) => (
                    <FormControl fullWidth>
                      <TextField
                        id="message-input"
                        minRows={2}
                        multiline
                        error={!!formState.errors.message}
                        {...field}
                      />
                    </FormControl>
                  )}
                />
                {errors.message ? (
                  <FormHelperText error>
                    Message is required (minimum {MIN_MESSAGE_LENGTH}{' '}
                    characters).
                  </FormHelperText>
                ) : (
                  <FormHelperText>{`minimum ${MIN_MESSAGE_LENGTH} characters`}</FormHelperText>
                )}
              </Box>
            </Stack>
          </form>
        </FormProvider>
      </DialogContent>
      <DialogActions>
        <Button
          color="primary"
          disabled={isLoadingUpdateStatus}
          onClick={handleCancel}
        >
          Cancel
        </Button>
        <Button
          disabled={isLoadingUpdateStatus}
          type="button"
          variant="contained"
          onClick={handleSubmit(onSubmit)}
        >
          {dialogTitle}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TicketStatusDialogThreat;
